import { Link } from 'react-router-dom';
import { servicesGridClass } from '@/lib/homeBlocks';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

// ─────────────────────────────────────────────────────────────
// Sections réutilisables de la page d'accueil publique (type Services).
// Chaque section est purement présentationnelle : les données sont
// fournies par la page d'accueil, qui décide aussi de l'ordre des blocs.
//
// Mise en page : toutes les grilles affichent 3 cartes par ligne sur
// ordinateur et 2 par ligne sur mobile (rythme 3×3×3 / 2×2×2).
// ─────────────────────────────────────────────────────────────

export interface ServiceRow {
  id: number;
  titre: string;
  description: string | null;
  product_image: string | null;
  prix: number | null;
  prix_promo: number | null;
  tags: string | null;
}

export interface ProjectRow {
  id: number;
  titre: string;
  description: string | null;
  product_image: string | null;
  prix: number | null;
  tags: string | null;
  ville: string | null;
  pays: string | null;
}

export interface PersonRow {
  id: number;
  titre: string;
  description: string | null;
  detailssup: string | null;
  product_image: string | null;
  tags: string | null;
  ville?: string | null;
  pays?: string | null;
}

export function coverImage(productImage: string | null | undefined): string {
  if (!productImage) return '';
  return (
    productImage
      .split('|')
      .filter(Boolean)
      .filter((url) => {
        const lower = url.toLowerCase();
        return !(lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov'));
      })[0] || ''
  );
}

const PLACEHOLDER =
  'https://readdy.ai/api/search-image?query=Clean%20minimal%20warm%20neutral%20gradient%20background%20with%20soft%20lighting%20and%20subtle%20geometric%20shapes%2C%20editorial%20style%2C%20calm%20elegant%20composition&width=800&height=600&seq=home-section-placeholder&orientation=landscape';

/** Habillage commun des cartes : léger relief, bord coloré au survol. */
const CARD_SHELL =
  'group relative flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80';

/** Bandeau dégradé en bas de l'image pour lire le texte par-dessus. */
const IMAGE_SCRIM =
  'absolute inset-0 bg-gradient-to-t from-foreground-950/75 via-foreground-950/10 to-transparent';

const TAG_CHIP =
  'px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary-100 text-secondary-800 whitespace-nowrap';

// ── En-tête de section avec lien « Voir plus » ──────────────────
export function SectionHeading({
  icon,
  title,
  linkTo,
  linkLabel = 'Voir plus',
}: {
  icon: string;
  title: string;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="flex items-end justify-between gap-3 mb-5 md:mb-7">
      <div className="min-w-0">
        <span className="block h-1 w-10 rounded-full bg-accent-500 mb-2.5"></span>
        <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className={`${icon} text-primary-500 text-base md:text-xl`}></i>
          <span className="truncate">{title}</span>
        </h2>
      </div>
      {linkTo && (
        <Link
          to={linkTo}
          className="flex items-center gap-1 text-xs md:text-sm text-primary-600 hover:text-primary-700 font-semibold cursor-pointer no-underline whitespace-nowrap flex-shrink-0"
        >
          {linkLabel} <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

// ── Carte service ───────────────────────────────────────────────
function ServiceCard({ service, monaie }: { service: ServiceRow; monaie: string }) {
  const img = coverImage(service.product_image);
  const { display, original } = useDisplayCurrency(monaie);
  const price = Number(service.prix || 0);
  const promo = service.prix_promo != null ? Number(service.prix_promo) : null;
  const shown = promo != null ? promo : price;

  return (
    <div className={CARD_SHELL}>
      <Link to={`/service/${service.id}`} className="relative block aspect-[16/10] overflow-hidden no-underline">
        <img
          src={img || PLACEHOLDER}
          alt={service.titre}
          title={`${service.titre} — service`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
          onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
        />
        <div className={IMAGE_SCRIM}></div>
        {shown > 0 && (
          <span className="absolute bottom-2.5 left-2.5 flex items-baseline gap-1.5 rounded-full bg-background-50/95 px-2.5 py-1">
            <span className="text-xs md:text-sm font-bold text-primary-700">{display(shown)}</span>
            {promo != null && (
              <span className="text-[10px] text-foreground-400 line-through">{display(price)}</span>
            )}
          </span>
        )}
        <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-background-50/90 flex items-center justify-center text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
          <i className="ri-arrow-right-up-line text-xs"></i>
        </span>
      </Link>
      <div className="flex flex-col flex-1 p-3.5 md:p-4">
        <Link to={`/service/${service.id}`} className="no-underline">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {service.titre}
          </h3>
        </Link>
        {service.description && (
          <p className="text-[11px] md:text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
            {service.description}
          </p>
        )}
        {service.tags && (
          <div className="flex items-center gap-1 flex-wrap mt-2.5">
            {service.tags.split(',').slice(0, 2).map((tag) => (
              <span key={tag} className={TAG_CHIP}>{tag.trim()}</span>
            ))}
          </div>
        )}
        <Link
          to={`/service/${service.id}`}
          className="mt-3 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] md:text-xs font-semibold bg-primary-50 text-primary-700 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors no-underline whitespace-nowrap"
        >
          <i className="ri-eye-line"></i>
          Voir le service
        </Link>
      </div>
    </div>
  );
}

export function ServicesGrid({
  services,
  monaie,
  badge,
  columns = 3,
}: {
  services: ServiceRow[];
  monaie: string;
  badge?: string;
  columns?: number;
}) {
  return (
    <div className={servicesGridClass(columns)}>
      {services.map((s) => (
        <div key={s.id} className="relative">
          {badge && (
            <span className="absolute top-2.5 left-2.5 z-10 px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-bold flex items-center gap-1">
              <i className="ri-star-fill text-[9px]"></i>
              {badge}
            </span>
          )}
          <ServiceCard service={s} monaie={monaie} />
        </div>
      ))}
    </div>
  );
}

// ── Portfolio ───────────────────────────────────────────────────
export function PortfolioGrid({ items, monaie }: { items: ProjectRow[]; monaie: string }) {
  const { display } = useDisplayCurrency(monaie);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
      {items.map((item) => (
        <Link
          key={item.id}
          to={`/portfolio?item=${item.id}`}
          className={`${CARD_SHELL} no-underline`}
        >
          <div className="relative aspect-[16/10] overflow-hidden bg-background-100">
            <img
              src={coverImage(item.product_image) || PLACEHOLDER}
              alt={item.titre}
              title={`${item.titre} — réalisation`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
              onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
            />
            <div className={IMAGE_SCRIM}></div>
            {Number(item.prix || 0) > 0 && (
              <span className="absolute bottom-2.5 left-2.5 px-2.5 py-1 rounded-full bg-background-50/95 text-[11px] font-bold text-primary-700 whitespace-nowrap">
                {display(Number(item.prix))}
              </span>
            )}
            <h3 className="absolute bottom-2.5 right-2.5 left-24 text-right text-xs md:text-sm font-semibold text-background-50 line-clamp-1">
              {item.titre}
            </h3>
          </div>
          <div className="flex flex-col flex-1 p-3.5 md:p-4">
            <p className="text-[11px] md:text-xs text-foreground-500 line-clamp-2 leading-relaxed min-h-[2rem]">
              {item.description || 'Projet réalisé par notre équipe.'}
            </p>
            {item.tags && (
              <div className="flex items-center gap-1 flex-wrap mt-2.5">
                {item.tags.split(',').slice(0, 2).map((tag) => (
                  <span key={tag} className={TAG_CHIP}>{tag.trim()}</span>
                ))}
              </div>
            )}
            <div className="mt-auto pt-3 flex items-center justify-between gap-2">
              <span className="text-[10px] md:text-[11px] text-foreground-400 truncate">
                <i className="ri-map-pin-line mr-1"></i>
                {item.ville && item.pays ? `${item.ville}, ${item.pays}` : item.ville || item.pays || 'Non spécifié'}
              </span>
              <span className="text-[11px] font-semibold text-primary-600 whitespace-nowrap">
                Voir <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ── Témoignages ─────────────────────────────────────────────────
export function TestimonialsGrid({ items }: { items: PersonRow[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
      {items.map((t) => (
        <div
          key={t.id}
          className="relative flex flex-col rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5 transition-colors hover:border-accent-300/80"
        >
          <i className="ri-double-quotes-l absolute top-3 right-4 text-2xl text-accent-200 leading-none"></i>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 flex-shrink-0">
              <img
                src={coverImage(t.product_image) || PLACEHOLDER}
                alt={t.titre}
                title={`${t.titre} — témoignage`}
                className="w-full h-full object-cover object-top"
                onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
              />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs md:text-sm font-semibold text-foreground-900 truncate">{t.titre}</h4>
              {t.tags && <p className="text-[10px] md:text-[11px] text-foreground-500 truncate">{t.tags.split(',')[0]}</p>}
            </div>
          </div>
          <p className="text-[11px] md:text-xs text-foreground-600 leading-relaxed line-clamp-4">{t.description}</p>
          <div className="mt-3 flex items-center gap-0.5 text-accent-500">
            {[0, 1, 2, 3, 4].map((i) => (
              <i key={i} className="ri-star-fill text-[10px]"></i>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── À propos / présentation ──────────────────────────────
export function AboutSection({ title, content }: { title?: string; content: string }) {
  return (
    <section className="py-10 md:py-16">
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 p-7 md:p-10 text-center">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400"></span>
          <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-text-line text-xl text-accent-700"></i>
          </div>
          {title && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">{title}</h2>
          )}
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line">{content}</p>
        </div>
      </div>
    </section>
  );
}

// ── Équipe ──────────────────────────────────────────────────────
export function TeamGrid({ members }: { members: PersonRow[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
      {members.map((m) => (
        <div key={m.id} className={`${CARD_SHELL} text-center`}>
          <div className="relative aspect-square overflow-hidden bg-background-100">
            <img
              src={coverImage(m.product_image) || PLACEHOLDER}
              alt={m.titre}
              title={`${m.titre} — équipe`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
              onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-2.5">
              <h3 className="text-[11px] md:text-sm font-semibold text-background-50 truncate">{m.titre}</h3>
              {m.description && (
                <p className="text-[10px] text-background-50/80 truncate">{m.description}</p>
              )}
            </div>
          </div>
          {m.tags && (
            <div className="flex items-center justify-center gap-1 flex-wrap p-2.5">
              {m.tags.split(',').slice(0, 2).map((tag) => (
                <span key={tag} className={TAG_CHIP}>{tag.trim()}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Partenaires ─────────────────────────────────────────────────
export function PartnersGrid({ partners }: { partners: PersonRow[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
      {partners.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-3 rounded-xl border border-background-200/70 bg-background-50 p-3 md:p-4 transition-colors hover:border-secondary-300"
        >
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
            <img
              src={coverImage(p.product_image) || PLACEHOLDER}
              alt={p.titre}
              title={`${p.titre} — partenaire`}
              className="w-full h-full object-cover object-top"
              onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
            />
          </div>
          <div className="min-w-0">
            <h3 className="text-xs md:text-sm font-semibold text-foreground-900 truncate">{p.titre}</h3>
            <p className="text-[10px] md:text-xs text-foreground-500 line-clamp-2">{p.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}